import axios from 'axios';
import urll from 'url';
import { axiosInstance } from 'utils/AxiosInstance';
import { API_PATHS } from 'utils/constants';
import { IRevisionCreate } from './../utils/contracts';

export const getrevisions = async (projectId: any) => {
  const data = await axiosInstance.get(`${API_PATHS.REVISIONS}`, {
    headers: {
      'x-project-id': projectId,
    },
  });
  return data.data;
};

export const deleteRevision = async (
  revisionId: string,
  projectId: any,
  organizationId: any
) => {
  await axiosInstance.delete(`${API_PATHS.DELETE_REVISION}/${revisionId}`, {
    headers: {
      'x-project-id': projectId,
      'x-organization-id': organizationId,
    },
  });
};

export const createRevision = async (
  revisionData: IRevisionCreate,
  projectId: any,
  organizationId: any,
  options?: { labsParse?: boolean }
) => {
  try {
    const data = await axiosInstance.post(
      `${API_PATHS.REVISIONS}`,
      {
        h_report_density: revisionData.h_report_density,
        h_report_percentage: revisionData.h_report_percentage,
        h_report_uvalue: revisionData.h_report_uvalue,
        notes: revisionData.notes,
        simulator: revisionData.simulator,
        skip_overwrite: revisionData.skip_overwrite,
      },
      {
        headers: {
          'x-project-id': projectId,
          'x-organization-id': organizationId,
        },
      }
    );

    if (data.status === 200 && data.data) {
      const s3PresignedUrlData = await getPresignedURL(
        data.data.id,
        projectId,
        organizationId
      );
      if (s3PresignedUrlData.status === 200 && s3PresignedUrlData.data) {
        const { publicId, url } = s3PresignedUrlData.data;

        await uploadFileToS3(revisionData.file, url, publicId);

        const { protocol, host, pathname } = urll.parse(url);

        const patchRevisionData: Record<string, unknown> = {
          files: {
            idf: {
              name: revisionData.file.name,
              url: `${protocol}//${host}${pathname}`,
              publicId: publicId,
            },
          },
        };
        if (options?.labsParse === true) {
          patchRevisionData.labsParse = true;
        }
        await patchRevisionAfterUpload(
          patchRevisionData,
          data.data.id,
          projectId,
          organizationId
        );
      }
      return data.data.id;
    } else {
      return false;
    }
  } catch (error) {
    console.log(error);
    throw error;

  }
};

export const updateRevision = async (
  revisionData: IRevisionCreate,
  revisionId: any,
  projectId: any,
  organizationId: any,
  options?: { labsParse?: boolean }
) => {
  try {
    let updateRevisionData: any = {
      h_report_density: revisionData.h_report_density,
      h_report_percentage: revisionData.h_report_percentage,
      h_report_uvalue: revisionData.h_report_uvalue,
      notes: revisionData.notes,
      skip_overwrite: revisionData.skip_overwrite,
    };
    if (options?.labsParse === true) {
      updateRevisionData.labsParse = true;
    }
    if (revisionData.file) {
      const s3PresignedUrlData = await getPresignedURL(
        revisionId,
        projectId,
        organizationId
      );

      if (s3PresignedUrlData.status === 200 && s3PresignedUrlData.data) {
        const { publicId, url } = s3PresignedUrlData.data;
        await uploadFileToS3(revisionData.file, url, publicId);

        const { protocol, host, pathname } = urll.parse(url);

        updateRevisionData.files = {
          idf: {
            name: revisionData.file.name,
            url: `${protocol}//${host}${pathname}`,
            publicId: publicId,
          },
        };
      }
    }

    const data = await axiosInstance.put(
      `${API_PATHS.REVISIONS}/${revisionId}`,
      updateRevisionData,
      {
        headers: {
          'x-project-id': projectId,
          'x-organization-id': organizationId,
        },
      }
    );
    if (data.status === 200 && data.data) {
      return {success: true};
    }
  } catch (error) {
    console.log(error);
    return {success: false, error: error};
  }
};

const uploadFileToS3 = async (file: any, presignedURL: any, key: string) => {
  const options = {
    params: {
      Key: key,
      ContentType: 'plain/text',
    },
    headers: {
      'Content-Type': 'plain/text',
      'Content-Length': file.size,
    },
  };

  return await axios.put(presignedURL, file, options);
};

const uploadDSBFileToS3 = async (file: any, presignedURL: any, publicId: string) => {
  const options = {
    params: {
      Key: publicId,
      ContentType: 'application/zip',
    },
    headers: {
      'Content-Type': 'application/zip',
      'Content-Length': file.size,
    },
  };

  return await axios.put(presignedURL, file, options);
};

export const runRevision = async (
  projectId: any,
  revisionId: any,
  organizationId: any
) => {
  return await axiosInstance.patch(
    `${API_PATHS.REVISIONS}/${revisionId}/run`,
    {},
    {
      headers: {
        'x-project-id': projectId,
        'x-organization-id': organizationId,
      },
    }
  );
};

export const getPresignedURL = async (
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  return await axiosInstance.get(
    `/pipe/${organizationId}/${projectId}/${revisionId}/upload/idf`
  );
};

export const getPresignedURLForDSBFile = async (
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  return await axiosInstance.get(
    `/pipe/${organizationId}/${projectId}/${revisionId}/upload/dsb`,
    {
      headers: {
        'Content-Type': 'application/zip',
      },
    }
  );
};

const patchRevisionAfterUpload = async (
  data: any,
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  return await axiosInstance.put(`${API_PATHS.REVISIONS}/${revisionId}`, data, {
    headers: {
      'x-project-id': projectId,
      'x-organization-id': organizationId,
    },
  });
};

export const unlockRevision = async (
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  return await axiosInstance.patch(
    `${API_PATHS.REVISIONS}/${revisionId}/unlock`,
    {},
    {
      headers: {
        'x-project-id': projectId,
        'x-organization-id': organizationId,
      },
    }
  );
};

export const getRevisionResult = async (
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  return await axiosInstance.get(
    `${API_PATHS.REVISIONS}/${revisionId}/result`,
    {
      headers: {
        'x-project-id': projectId,
        'x-organization-id': organizationId,
      },
    }
  );
};

export const getRevisionById = async (
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  return await axiosInstance.get(`${API_PATHS.REVISIONS}/${revisionId}`, {
    headers: {
      'x-project-id': projectId,
      'x-organization-id': organizationId,
    },
  });
};

export const getRevisionByIdWithProject = async (
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  const headers: any = {
    'x-project-id': projectId,
  };
  // Only include organization-id header if it's provided (for inspectors, it may be undefined or different)
  if (organizationId) {
    headers['x-organization-id'] = organizationId;
  }
  const response = await axiosInstance.get(`${API_PATHS.REVISIONS}/${revisionId}/with-project`, {
    headers,
  });
  return response.data;
};

export const updateDSBFileToTicket = async (
  projectId: any,
  organizationId: any,
  revisionId: any,
  file: any
) => {
  try {
    let updateRevisionData: any = {};
    if (file) {
      const s3PresignedUrlData = await getPresignedURLForDSBFile(
        revisionId,
        projectId,
        organizationId
      );

      if (s3PresignedUrlData.status === 200 && s3PresignedUrlData.data) {
        const { publicId, url } = s3PresignedUrlData.data;
        await uploadDSBFileToS3(file, url, publicId);

        const { protocol, host, pathname } = urll.parse(url);

        updateRevisionData.files = {
          dsb: {
            name: file.name,
            url: `${protocol}//${host}${pathname}`,
            publicId: publicId,
          },
        };
      }
    }

    const data = await axiosInstance.patch(
      `${API_PATHS.TICKETS}/${revisionId}/open`,
      updateRevisionData,
      {
        headers: {
          'x-project-id': projectId,
          'x-organization-id': organizationId,
        },
      }
    );

    if (data.status === 200 && data.data) {
      return true;
    }
  } catch (error) {
    console.log(error);
    return false;
  }
  
};

export const getFeatures = async () => {
  const response = await axiosInstance.get(`${API_PATHS.FEATURES}`);
  return response.data;
};

export const generateViewerUrl = async (revisionId: any) => {
  const response = await axiosInstance.post(`${API_PATHS.REVISIONS}/${revisionId}/create-public-url`);
return response.data
};

export const getViewerUrl = async (revisionId: any) => {
  const response = await axiosInstance.get(`${API_PATHS.REVISIONS}/${revisionId}/get-public-url`);
  return response.data;
};

export const killRevision = async (
  revisionId: any,
  projectId: any,
  organizationId: any
) => {
  // Call the kill endpoint for a revision
  return await axiosInstance.patch(
    `${API_PATHS.REVISIONS}/${revisionId}/kill`,
    {},
    {
      headers: {
        'x-project-id': projectId,
        'x-organization-id': organizationId,
      },
    }
  );
};

export const getReportJson = async (revisionId: string) => {
  const response = await axiosInstance.get(`/inspections/${revisionId}/data`);
  return response.data;
};

/**
 * Triggers IDF -> epJSON conversion for a revision that has already passed
 * parsing (status === 'checked'). Admins may pass force=true to convert even
 * when validation failed. Returns { success, revision } or { success: false, errors }.
 */
export const prepareRevision = async (
  revisionId: any,
  projectId: any,
  organizationId: any,
  options?: { force?: boolean; idfVersion?: string | null; fixNames?: boolean }
) => {
  const response = await axiosInstance.post(
    `${API_PATHS.REVISIONS}/${revisionId}/prepare`,
    {
      force: options?.force === true,
      ...(options?.fixNames ? { fixNames: true } : {}),
      ...(options?.idfVersion ? { idfVersion: options.idfVersion } : {}),
    },
    {
      headers: {
        'x-project-id': projectId,
        'x-organization-id': organizationId,
      },
    }
  );
  return response.data;
};